import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { logoUrlFor } from "../../lib/logo";
import { toast } from "sonner";
import { Building2, Save, FileText, Upload, ImageOff, RotateCcw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const EMPTY = {
  name: "", address_lines: [], gstin: "", email: "", phone: "", site: "",
  logo_version: 0, logo_storage_path: null,
};

export default function AdminCompanySettings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/company").then((r) => {
      setC({ ...EMPTY, ...r.data });
      setPreviewVersion(r.data.logo_version || 0);
    }).catch(() => {});
  }, []);

  const upd = (k, v) => setC((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!c?.name?.trim()) return toast.error("Company name is required");
    if (c.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/.test(c.gstin.toUpperCase())) {
      return toast.error("Invalid GSTIN format (expected 15 chars like 29AANCD0102E1Z3)");
    }
    setSaving(true);
    try {
      const payload = {
        name: c.name.trim(),
        address_lines: (typeof c.address_lines === "string"
          ? c.address_lines.split("\n") : (c.address_lines || [])
        ).map((l) => l.trim()).filter(Boolean),
        gstin: (c.gstin || "").trim().toUpperCase(),
        email: (c.email || "").trim(),
        phone: (c.phone || "").trim(),
        site: (c.site || "").trim(),
      };
      const r = await api.post("/admin/company", payload);
      setC({ ...EMPTY, ...r.data.company });
      toast.success("Company details saved — invoices will use these values");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onPickFile = () => fileRef.current?.click();

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Logo too large (max 4 MB)");
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      return toast.error("Use PNG, JPG, WEBP or SVG");
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      // Do NOT set Content-Type — axios auto-sets multipart with the correct
      // boundary when the payload is FormData. Setting it manually breaks
      // the multipart parser on the server.
      const r = await api.post("/admin/company/logo", fd);
      setC(s => ({ ...s, logo_version: r.data.logo_version, logo_storage_path: "uploaded" }));
      setPreviewVersion(r.data.logo_version);
      toast.success("Logo updated — refresh other tabs to see it everywhere");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetLogo = async () => {
    if (!window.confirm("Reset to the packaged default logo? Your uploaded logo will be removed.")) return;
    try {
      await api.delete("/admin/company/logo");
      setC(s => ({ ...s, logo_version: 0, logo_storage_path: null }));
      setPreviewVersion(Date.now());
      toast.success("Reverted to the default logo");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reset"); }
  };

  if (!c) return <div className="text-stone-500">Loading…</div>;

  const addressStr = Array.isArray(c.address_lines) ? c.address_lines.join("\n") : (c.address_lines || "");
  const logoSrc = logoUrlFor(previewVersion || Date.now());
  const hasCustomLogo = !!c.logo_storage_path;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#D4AF37]" /> Company details
          </h1>
          <div className="text-sm text-stone-500">
            These values are printed on every GST invoice PDF and shown on the storefront footer + contact page.
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-company-btn"
          className="bg-[#3B2F2F] text-white rounded-md px-5 py-2 flex items-center gap-2 disabled:bg-stone-400 hover:bg-black">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save details"}
        </button>
      </div>

      {/* Brand logo uploader */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-32 h-32 rounded-2xl border border-stone-200 bg-[#FDFBF7] flex items-center justify-center overflow-hidden">
            <img key={previewVersion} src={logoSrc} alt="Current brand logo"
                  className="max-w-[110px] max-h-[110px] object-contain"
                  data-testid="company-logo-preview"
                  onError={(e)=>{e.currentTarget.style.display="none";}}/>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="font-outfit font-semibold text-[#3B2F2F] text-lg">Brand logo</div>
              {hasCustomLogo ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8EDDF] text-[#2E4A35] uppercase tracking-widest">Custom</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 uppercase tracking-widest">Default</span>
              )}
            </div>
            <p className="text-sm text-stone-500 mb-3">
              Shown across the storefront header + footer, admin sidebar, admin login, checkout modal, PO PDFs and tax-invoice PDFs. Recommended: a square PNG or SVG under 4 MB, ideally 512×512 or larger with a transparent background.
            </p>
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" className="hidden"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={(e)=>uploadLogo(e.target.files?.[0])}
                      data-testid="company-logo-input"/>
              <button onClick={onPickFile} disabled={uploading}
                      data-testid="company-logo-upload"
                      className="bg-[#3B2F2F] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-black disabled:opacity-60 flex items-center gap-2">
                <Upload className="w-4 h-4"/>
                {uploading ? "Uploading…" : (hasCustomLogo ? "Replace logo" : "Upload logo")}
              </button>
              {hasCustomLogo && (
                <button onClick={resetLogo}
                        data-testid="company-logo-reset"
                        className="border border-stone-300 text-stone-700 rounded-full px-5 py-2 text-sm font-medium hover:bg-stone-50 flex items-center gap-2">
                  <RotateCcw className="w-4 h-4"/> Reset to default
                </button>
              )}
            </div>
            <p className="text-[11px] text-stone-400 mt-2">
              After uploading, hard-refresh other browser tabs (Ctrl/⌘ + Shift + R) to see the new logo instantly.
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
          <Field label="Legal / Company Name">
            <Input value={c.name || ""} onChange={(e) => upd("name", e.target.value)} data-testid="company-name" placeholder="DailyBeans Foods & Beverages Pvt Ltd" />
          </Field>
          <Field label="Registered Address (one line per row)">
            <Textarea rows={4} value={addressStr} onChange={(e) => upd("address_lines", e.target.value)} data-testid="company-address" placeholder="#05, Balaji Serenity, Andhapura&#10;Electronics City 2, Bengaluru 560100&#10;Karnataka, India" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="GSTIN (15 chars, e.g. 29AANCD0102E1Z3)">
              <Input value={c.gstin || ""} onChange={(e) => upd("gstin", e.target.value.toUpperCase())} data-testid="company-gstin" placeholder="29AANCD0102E1Z3" className="font-mono" />
            </Field>
            <Field label="Business Email">
              <Input type="email" value={c.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid="company-email" placeholder="admin@mydailybean.com" />
            </Field>
            <Field label="Phone">
              <Input value={c.phone || ""} onChange={(e) => upd("phone", e.target.value)} data-testid="company-phone" placeholder="+91-80-4000-0000" />
            </Field>
            <Field label="Website">
              <Input value={c.site || ""} onChange={(e) => upd("site", e.target.value)} data-testid="company-site" placeholder="https://mydailybean.com" />
            </Field>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#FDFBF7] to-[#FEF3E2] border border-[#D4AF37]/40 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            <div className="font-outfit font-semibold text-[#3B2F2F]">Invoice preview</div>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <img src={logoSrc} alt="" className="w-10 h-10 object-contain"
                  onError={(e)=>{e.currentTarget.style.display="none";}}/>
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">TAX INVOICE</div>
          </div>
          <div className="font-serif-display text-2xl text-[#3B2F2F] mt-2">{c.name || "Company name"}</div>
          <div className="text-sm text-stone-600 mt-3 space-y-0.5 leading-relaxed">
            {(Array.isArray(c.address_lines) ? c.address_lines : addressStr.split("\n")).map((l, i) => (
              <div key={i}>{l || <span className="text-stone-300">—</span>}</div>
            ))}
            <div className="pt-2 mt-2 border-t border-dashed border-stone-300">
              <div className="font-mono text-xs">GSTIN: {c.gstin || "—"}</div>
              <div className="text-xs mt-1">{c.email || "—"} · {c.phone || "—"}</div>
            </div>
          </div>
          <div className="text-[10px] text-stone-500 mt-4 italic">
            Preview only — the actual invoice PDF includes the full order, items table and totals below this header.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}
